import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'next/router'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import log from 'sentry/log'
import { event } from '@blocket/tracking-pulse'
import { TextHeadline3, TextBody, Button, colors } from '@blocket/blocket-ui'
import { mediaQueries, withDevice } from 'plugins/device'
import { actionCreators as api } from 'reduxBits/api'
import TabBar from 'components/sharedComponents/TabBar'
import Footer from 'components/sharedComponents/Footer'
import { profilePagePublicView } from 'tracking/profilePagePublic'
import { ResponsiveColumnWrapper } from 'components/sharedComponents/LayoutStyles'
import ErrorComponent from 'components/sharedComponents/ErrorComponent'
import staticFilesUrl from 'constants/staticFiles'
import Loading from './components/Loading'
import UserInfo from './components/UserInfo'
import AdsCards from './components/AdsCards'
import ReviewsInfo from './components/ReviewsInfo'
import settings from '../RatingReviews/sharedUtils/settings'

const Wrapper = styled.div`
  min-height: 100vh;
  background-color: ${colors.grey100};
  display: flex;
  flex-direction: column;
`

const ColorBanner = styled.div`
  background-image: url('${staticFilesUrl}/assets/images/color-banner-small.svg');
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  @media ${mediaQueries.large} {
    background-image: url('${staticFilesUrl}/assets/images/color-banner-big.svg');
    background-repeat: no-repeat;
    background-size: cover;
  }
`

const SecondContentWrapper = styled(ResponsiveColumnWrapper)`
  padding: 0 16px;
  margin-bottom: 16px;
  @media ${mediaQueries.large} {
    padding: 0;
  }
`

const NoAdsWrapper = styled.div`
  border: 1px solid ${colors.grey300};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  padding: 26px;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: ${colors.white100};
  margin-bottom: 20px;
  @media ${mediaQueries.large} {
    flex-direction: row;
    text-align: left;
    margin-bottom: 40px;
  }
`

const NoAdsText = styled(TextBody)`
  margin: 0px 0px 24px 0px;
  @media ${mediaQueries.large} {
    margin: 0px 24px 0px 9px;
  }
`

const FullWidthWrapper = styled.div`
  background-color: ${colors.grey100};
  display: flex;
  justify-content: center;
  flex-grow: 1;
  margin-top: 50px;
`

const AdsHeading = styled.div`
  display: flex;
  align-items: center;
`

const ActiveAdsCount = styled(TextBody)`
  margin-left: 4px;
  color: ${colors.grey700};
`

const PublicProfilePage = ({ publicProfileAccountId, router, device }) => {
  const [profileData, setProfileData] = useState()
  const [trackingViewSent, setTrackingViewSent] = useState(false)
  const [profileError, setProfileError] = useState()
  const [activeAdCountOnBlocket, setActiveAdCountOnBlocket] = useState()
  const [userSdrn, setUserSdrn] = useState('')
  const baseUrl = useSelector((state) => state.config.urls.base)
  const apiUrl = useSelector((state) => state.config.urls.api)

  const dispatch = useDispatch()

  const publicProfileBlocketId = router.query.blocketId
  const hasActiveAds = Boolean(profileData?.activeAds?.ads?.length)

  const hasReviews = profileData?.profileInfo.hasReviews
  const loadTrustSettings = hasReviews && Boolean(userSdrn)
  const isMobile = !device.is('medium')
  const numberOfReviewColumns = isMobile ? 1 : 2

  useEffect(() => {
    if (publicProfileAccountId) {
      const fetchData = async () => {
        try {
          const response = await dispatch(
            api.fetch(`profile-be/v1/public-profiles/${publicProfileAccountId}`),
          )
          setProfileData(response)
        } catch (error) {
          setProfileError(error.response.status.toString())
        }
      }
      fetchData()
    }
  }, [dispatch, publicProfileAccountId])

  useEffect(() => {
    if (loadTrustSettings) {
      const loadRatingReviewsPackage = async () => {
        const trust = await import('@nmp-trust/all')
        trust.setupTracker(event)
      }
      loadRatingReviewsPackage()
    }
  }, [loadTrustSettings])

  useEffect(() => {
    if (!userSdrn && publicProfileBlocketId && hasReviews) {
      const fetchData = async () => {
        try {
          const response = await dispatch(
            api.fetch(`ratings-and-reviews/v1/sdrn/${publicProfileBlocketId}`, {
              method: 'GET',
            }),
          )
          setUserSdrn(response.sdrn)
        } catch (error) {
          log.message(
            `Public profile error from 'ratings-and-reviews/v1/sdrn/${blocketId}': ${error}`,
          )
        }
      }
      fetchData()
    }
  }, [userSdrn, dispatch, publicProfileBlocketId, hasReviews])

  useEffect(() => {
    if (profileData && !hasActiveAds) {
      const fetchData = async () => {
        const response = await dispatch(
          api.fetch(`classifieds/v1/ad_counters?status=active&include=all`),
        )
        setActiveAdCountOnBlocket(response.totalCount)
      }
      fetchData()
    }
  }, [dispatch, profileData, hasActiveAds])

  useEffect(() => {
    if (!trackingViewSent) {
      profilePagePublicView()
      setTrackingViewSent(true)
    }
  }, [trackingViewSent])

  const clickShowAdsOnBlocket = () => {
    window.location = `${baseUrl}/annonser/hela_sverige`
  }

  if (profileError) {
    return <ErrorComponent error={profileError} />
  }

  return (
    <Wrapper>
      <TabBar />
      <ColorBanner>
        {!profileData ? (
          <Loading />
        ) : (
          <UserInfo
            profileData={profileData}
            userSdrn={userSdrn}
            apiUrl={apiUrl}
            loadTrustSettings={loadTrustSettings}
            isMobile={isMobile}
          />
        )}
      </ColorBanner>
      <FullWidthWrapper>
        <SecondContentWrapper>
          {profileData && !hasActiveAds && (
            <NoAdsWrapper>
              <NoAdsText>
                Den här säljaren har just nu inga annonser. Men vi har {activeAdCountOnBlocket}{' '}
                andra annonser på Blocket.
              </NoAdsText>
              <Button size="tiny" type="primary" onClick={clickShowAdsOnBlocket}>
                Sök bland annonser
              </Button>
            </NoAdsWrapper>
          )}
          {hasActiveAds && (
            <>
              <AdsHeading>
                <TextHeadline3 tagName="h2">{profileData.activeAds.labelPrimary}</TextHeadline3>
                <ActiveAdsCount>{profileData.activeAds.labelSecondary}</ActiveAdsCount>
              </AdsHeading>
              <AdsCards ads={profileData.activeAds.ads} />
            </>
          )}
          {loadTrustSettings && (
            <>
              <ReviewsInfo />
              <trust-settings settings={settings({ apiUrl })}>
                <a id="summary-link-target"></a>
                <trust-public-listing
                  user={userSdrn}
                  columns={numberOfReviewColumns}
                  expandable
                ></trust-public-listing>
              </trust-settings>
            </>
          )}
        </SecondContentWrapper>
      </FullWidthWrapper>
      <Footer />
    </Wrapper>
  )
}

PublicProfilePage.propTypes = {
  publicProfileAccountId: PropTypes.string.isRequired,
  router: PropTypes.object.isRequired,
  device: PropTypes.object.isRequired,
}

export default withRouter(withDevice(PublicProfilePage))
